/**
 * Lobby State for Poker Reborn
 */

import { signal, computed, Signal } from './Signal';
import type { GameManager, TableInfo, TableSize, Stakes, StakesLevel } from '@/types/game';
import type { GameType } from '@/config/managers';

// =============================================================================
// TYPES
// =============================================================================

/** Table size filter */
export type TableSizeFilter = TableSize | 'all';

/** Stakes filter */
export type StakesFilter = 'all' | 'micro' | 'low' | 'mid' | 'high';

// =============================================================================
// LOBBY STATE
// =============================================================================

export interface LobbyStateType {
  // Data
  managers: Signal<GameManager[]>;
  tables: Signal<TableInfo[]>;
  isLoading: Signal<boolean>;

  // Filters
  selectedManagerId: Signal<string | null>;
  tableSizeFilter: Signal<TableSizeFilter>;
  stakesFilter: Signal<StakesFilter>;
  searchQuery: Signal<string>;

  // Game type (T37)
  selectedGameType: Signal<GameType>;

  // Computed
  selectedManager: Signal<GameManager | null>;
  filteredTables: Signal<TableInfo[]>;
  tableCount: Signal<number>;
}

function createLobbyState(): LobbyStateType {
  // Data
  const managers = signal<GameManager[]>([]);
  const tables = signal<TableInfo[]>([]);
  const isLoading = signal(false);

  // Filters
  const selectedManagerId = signal<string | null>(null);
  const tableSizeFilter = signal<TableSizeFilter>('all');
  const stakesFilter = signal<StakesFilter>('all');
  const searchQuery = signal('');

  // Game type (T37)
  const selectedGameType = signal<GameType>('cash');

  // Computed: Selected manager
  const selectedManager = computed(
    () => {
      const id = selectedManagerId.get();
      if (!id) return null;
      return managers.get().find((m) => m.id === id) ?? null;
    },
    [managers, selectedManagerId]
  );

  // Computed: Filtered tables
  const filteredTables = computed(
    () => {
      // Use explicit tables if set, otherwise use selected manager's tables
      let tablesToFilter = tables.get();
      if (tablesToFilter.length === 0) {
        const manager = selectedManager.get();
        if (manager) {
          tablesToFilter = manager.tables;
        }
      }

      if (tablesToFilter.length === 0) return [];

      let result = [...tablesToFilter];

      // Filter by table size
      const sizeFilter = tableSizeFilter.get();
      if (sizeFilter !== 'all') {
        result = result.filter((t) => t.seats === sizeFilter);
      }

      // Filter by stakes
      const stFilter = stakesFilter.get();
      if (stFilter !== 'all') {
        result = result.filter((t) => matchesStakesFilter(t.stakes, stFilter));
      }

      // Filter by search
      const query = searchQuery.get().toLowerCase();
      if (query) {
        result = result.filter((t) =>
          t.name.toLowerCase().includes(query) ||
          t.id.toLowerCase().includes(query)
        );
      }

      return result;
    },
    [tables, selectedManager, tableSizeFilter, stakesFilter, searchQuery]
  );

  // Computed: Table count
  const tableCount = computed(
    () => filteredTables.get().length,
    [filteredTables]
  );

  return {
    managers,
    tables,
    isLoading,
    selectedManagerId,
    tableSizeFilter,
    stakesFilter,
    searchQuery,
    selectedGameType,
    selectedManager,
    filteredTables,
    tableCount,
  };
}

/** Stakes filter matcher */
function matchesStakesFilter(stakes: Stakes, filter: StakesFilter): boolean {
  const bb = stakes.bigBlind;

  switch (filter) {
    case 'micro':
      return bb <= 2;
    case 'low':
      return bb > 2 && bb <= 10;
    case 'mid':
      return bb > 10 && bb <= 50;
    case 'high':
      return bb > 50;
    default:
      return true;
  }
}

/** Global lobby state */
export const lobbyState = createLobbyState();

// =============================================================================
// ACTIONS
// =============================================================================

/** Set managers list */
export function setManagers(newManagers: GameManager[]): void {
  lobbyState.managers.set(newManagers);
}

/** Set tables list */
export function setTables(newTables: TableInfo[]): void {
  lobbyState.tables.set(newTables);
}

/** Set loading state */
export function setLoading(loading: boolean): void {
  lobbyState.isLoading.set(loading);
}

/** Select a manager by ID */
export function selectManager(managerId: string | null): void {
  lobbyState.selectedManagerId.set(managerId);
  // Clear tables when switching managers
  lobbyState.tables.set([]);
}

/** Set selected manager directly */
export function setSelectedManager(manager: GameManager | null): void {
  lobbyState.selectedManagerId.set(manager?.id ?? null);
  if (manager) {
    lobbyState.tables.set(manager.tables);
  } else {
    lobbyState.tables.set([]);
  }
}

/** Set table size filter */
export function setTableSizeFilter(filter: TableSizeFilter): void {
  lobbyState.tableSizeFilter.set(filter);
}

/** Set size filter (alias using TableSize) */
export function setSizeFilter(size: TableSize | 'all'): void {
  lobbyState.tableSizeFilter.set(size);
}

/** Set stakes filter */
export function setStakesFilter(filter: StakesLevel | 'all'): void {
  lobbyState.stakesFilter.set(filter);
}

/** Set search query */
export function setSearchQuery(query: string): void {
  lobbyState.searchQuery.set(query);
}

/** Clear all filters */
export function clearFilters(): void {
  lobbyState.tableSizeFilter.set('all');
  lobbyState.stakesFilter.set('all');
  lobbyState.searchQuery.set('');
}

/** Set selected game type (T37) */
export function setSelectedGameType(gameType: GameType): void {
  lobbyState.selectedGameType.set(gameType);
}

/** Get table by ID */
export function getTable(tableId: string): TableInfo | null {
  // Check current tables first
  const currentTables = lobbyState.tables.get();
  const tableInCurrent = currentTables.find((t) => t.id === tableId);
  if (tableInCurrent) return tableInCurrent;

  // Check all managers
  for (const manager of lobbyState.managers.get()) {
    const table = manager.tables.find((t) => t.id === tableId);
    if (table) return table;
  }
  return null;
}
